import React, { ForwardedRef } from 'react';
import clsx from 'clsx';
import { useTabList } from '@react-aria/tabs';
import { filterDOMProps, mergeProps, useObjectRef } from '@react-aria/utils';
import { useTabListState } from '@react-stately/tabs';
import styles from '@danske/sapphire-css/components/buttonGroup/buttonGroup.module.css';

import { ButtonTab } from './ButtonTab';
import { TabPanelWrapper } from '../TabPanelWrapper';
import { TabsProps } from '../TabsProps';
import { useSapphireStyleProps, useThemeCheck } from '../../../utils';

function Tabs<T extends object>(
  { align = 'left', size = 'md', ...props }: TabsProps<T>,
  ref: ForwardedRef<HTMLDivElement>
): JSX.Element {
  useThemeCheck();

  const tabListRef = useObjectRef(ref);
  const state = useTabListState({
    ...props,
    isDisabled: props.isDisabled,
    children: props.children,
  });

  const { styleProps } = useSapphireStyleProps(props);
  const { tabListProps } = useTabList({ ...props }, state, tabListRef);
  const domProps = filterDOMProps(props, { global: true });

  return (
    <>
      <div
        ref={tabListRef}
        {...mergeProps(tabListProps, styleProps, domProps, {
          className: clsx(
            styles['sapphire-button-group'],
            styles['sapphire-button-group--spacing-sm'],
            styles[`sapphire-button-group--align-${align}` as const]
          ),
        })}
      >
        {Array.from(state.collection).map((item) => (
          <ButtonTab key={item.key} item={item} state={state} size={size} />
        ))}
      </div>
      <TabPanelWrapper state={state} keepMounted={props.keepMounted} />
    </>
  );
}

// NOTE: couldn't use ts-expect-error because in storybook the error is inconsistent in building and storybook typechecking
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore Expression produces a union type that is too complex to represent.
export const ButtonTabs = React.forwardRef(Tabs);
